import FlexSearch from 'flexsearch'
import { DateTime } from 'luxon'
import * as R from 'ramda'

import { flightComparator, FlightType, mapFlightDetails, mapFlightListItem } from './flightDetailsMapper.js'

function create (app, config) {
  const createIndex = () => new FlexSearch.Index({
    tokenize: 'forward'
  })
  const init = async () => {
    state.tz = await app.bus.get('venueData/getVenueTimezone')
  }

  const state = {
    lastUpdated: 0,
    flights: { dep: null, arr: null },
    index: { dep: createIndex(), arr: createIndex() },
    gates: null,
    apiError: false
  }

  const enrichData = (flights, airportsArg, airlinesArg, flightType) => {
    const airports = airportsArg.reduce((acc, obj) => { return { ...acc, [obj.iata]: obj } }, {})
    const airlines = airlinesArg.flatMap((airline) => [
      { code: airline.iata, airline },
      { code: airline.icao, airline }
    ])
      .filter(({ code }) => code)
      .reduce((acc, { code, airline }) => { return { ...acc, [code]: airline } }, {})
    return flights.map(flight => {
      const gateId = flightType === FlightType.DEPARTURE
        ? R.pathOr('', ['airportResources', 'departureGate'], flight)
        : R.pathOr('', ['airportResources', 'arrivalGate'], flight)
      const gatePoi = state.gates[gateId]
      return {
        ...flight,
        flightType,
        gatePoi,
        airline: airlines[flight.carrierFsCode],
        departureAirport: airports[flight.departureAirportFsCode],
        arrivalAirport: airports[flight.arrivalAirportFsCode]
      }
    })
  }

  const updateIndexes = (type, flightStatuses) => {
    state.index[type] = createIndex()
    state.flights[type] = {}
    flightStatuses.forEach(status => {
      state.index[type].add(status.flightId, createFlightSearchEntry(status))
      state.flights[type][status.flightId] = status
    })
  }

  const searchPropertiesPaths = [
    ['flightNumber'],
    ['airline', 'iata'],
    ['airline', 'name'],
    ['arrivalAirport', 'iata'],
    ['arrivalAirport', 'city'],
    ['departureAirport', 'iata'],
    ['departureAirport', 'city']
  ]
  const createFlightSearchEntry = R.pipe( // apply each function one after another to passed object
    R.juxt(R.map(R.path, searchPropertiesPaths)), // safe collect of nested properties into list
    R.filter(R.identity), // filter 'falsy' values
    R.join(' ')
  )

  const indexGatePois = async () => {
    if (state.gates === null) {
      const gatePois = await app.bus.get('poi/getByCategoryId', { categoryId: 'gate' })
      const parseGate = ({ name }) => R.tail(name.replace(',', '').split(' '))
      const pairs = R.reduce((acc, poi) => {
        const gateIds = parseGate(poi)
        const gateAndPoiPairs = R.map(gateId => [gateId, poi], gateIds)
        return [...acc, ...gateAndPoiPairs]
      }, [], Object.values(gatePois))

      state.gates = R.fromPairs(pairs)
    }
    return state
  }

  const updateFlights = async () => {
    await indexGatePois()
    await indexGatePois()
    const fallbackVenueId = await app.bus.get('venueData/getVenueId')
    const configuredVenueId = app?.config?.plugins?.venueDataLoader?.venueId
    const venueIdToUse = configuredVenueId ?? fallbackVenueId
    const params = new URLSearchParams()
    const locale = app.i18n?.().language
    if (locale) {
      params.append('locale', locale)
    }
    if (config?.timeRange) {
      const now = Date.now()
      const startDate = new Date(now - config.timeRange.beforeNowMs).toISOString()
      const endDate = new Date(now + config.timeRange.afterNowMs).toISOString()
      params.append('startDate', startDate)
      params.append('endDate', endDate)
    }

    const baseURL = `https://marketplace.locuslabs.com/venueId/${venueIdToUse}/flight-status`
    const apiURL = params.toString()
      ? `${baseURL}?${params.toString()}`
      : baseURL

    try {
      const response = await fetch(apiURL)
      const data = await response.json();
      [FlightType.ARRIVAL, FlightType.DEPARTURE].forEach(type => {
        const key = type === FlightType.DEPARTURE ? 'departures' : 'arrivals'
        const { airports, airlines } = data.data[key].appendix
        updateIndexes(type, enrichData(data.data[key].flightStatuses, airports, airlines, type))
      })
      state.apiError = false
    } catch (error) {
      state.apiError = true
      console.error('Error from flight status api call', error);
      [FlightType.ARRIVAL, FlightType.DEPARTURE].forEach(type => {
        // In the case of an api error we still need to update current flights to an empty array, otherwise we get an object undefined error
        updateIndexes(type, [])
      })
    } finally {
      state.lastUpdated = Date.now()
    }
    return state
  }

  const checkFlights = async () => {
    // if the data was not update in the last minute, update it
    if (Date.now() - state.lastUpdated > 60 * 1000) {
      await updateFlights()
    }
  }

  /**
 * Does query for flight status info. Before doing anything requests verification
 * if the date is up-to-date
 * @param  {String} term - term to query the search index, default null, if null returns all flights of provided type
 * @param  {String} type - type of flights to query for, default 'dep' (departures)
 */
  app.bus.on('flightStatus/query', async ({ term = null, type = FlightType.DEPARTURE } = {}) => {
    await checkFlights()
    if (term) {
      const ids = state.index[type].search({ query: term })
      return { flights: ids.map(id => state.flights[type][id]), apiError: state.apiError }
    } else {
      return { flights: Object.values(state.flights[type]), apiError: state.apiError }
    }
  })

  /**
 * Fetches flight info based on flight ID, make sure the data is up-tp-date first
 * @param  {String} flightId - id of the flight we want to get
 */
  app.bus.on('flightStatus/getFlight', async ({ flightId }) => {
    await checkFlights()
    const flight = state.flights.dep[flightId] || state.flights.arr[flightId]
    return flight
  })

  app.bus.on('flightStatus/mapFlightDetails', async ({ flight }) => {
    return mapFlightDetails(DateTime.local(), flight, app.gt(), state.tz, app.i18n().language)
  })

  app.bus.on('flightStatus/mapFlightListItems', async ({ flights }) => {
    return flights.map(flight => mapFlightListItem(DateTime.local(), flight, app.gt(), state.tz, app.i18n().language))
      .sort(flightComparator)
  })

  return {
    init,
    internal: {
      updateFlights,
      indexGatePois
    }
  }
}

export {
  create
}
